/**
 * MKS-339 — formatting helpers for the helpdesk inbox's SLA columns.
 *
 * Kept out of the components so the rules a reader has to trust (when a
 * number becomes a colour, how a duration reads) are testable without
 * rendering anything.
 */
import type {
  HelpdeskSlaMilestone,
  HelpdeskSlaState,
  HelpdeskStatus,
} from '../../services/api/helpdesk';
import { formatDayMonthShort } from '../../utils/dateFormat';

/**
 * A duration as a person reads it on a support queue: minutes under an hour,
 * then hours, then days. Never "0.03 days" and never a seconds column nobody
 * looks at.
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0m';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rem = minutes % 60;
    return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours === 0 ? `${days}d` : `${days}d ${remHours}h`;
}

/**
 * One milestone as a single cell: elapsed, and the target when there is one.
 * A request with no tier has no target, and saying "2h / —" is clearer than
 * inventing a denominator.
 */
export function formatMilestone(milestone: HelpdeskSlaMilestone): string {
  const elapsed = formatDuration(milestone.elapsedMs);
  if (milestone.targetMinutes == null) return elapsed;
  return `${elapsed} / ${formatDuration(milestone.targetMinutes * 60_000)}`;
}

export interface SlaTone {
  bg: string;
  fg: string;
  label: string;
}

/**
 * The four states as Pill colours. Semantic colour flows through inline
 * style per the v7 Pill contract, so these are literal values rather than
 * Tailwind classes.
 */
export const SLA_TONES: Record<HelpdeskSlaState, SlaTone> = {
  none: { bg: '#F1F0ED', fg: '#6B6B72', label: 'No SLA' },
  ok: { bg: '#E7F5EC', fg: '#1C7A41', label: 'On track' },
  warning: { bg: '#FDF2E2', fg: '#A0521D', label: 'Breaching soon' },
  breached: { bg: '#FDE8E8', fg: '#B42318', label: 'Breached' },
};

export const STATUS_TONES: Record<HelpdeskStatus, SlaTone> = {
  triage: { bg: '#FDF2E2', fg: '#A0521D', label: 'Needs triage' },
  open: { bg: '#EAF1FB', fg: '#1C4E8A', label: 'Open' },
  closed: { bg: '#F1F0ED', fg: '#6B6B72', label: 'Closed' },
};

/**
 * "2 hours ago" for the last-activity column. Anything older than a week
 * reads as a date — "9 days ago" is already less useful than "6 Oct".
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const ms = now.getTime() - then.getTime();
  if (!Number.isFinite(ms)) return '—';
  if (ms < 60_000) return 'just now';
  if (ms < 7 * 24 * 60 * 60_000) return `${formatDuration(ms)} ago`;
  // MKS-351: `20 Nov` from the shared util — English month, no browser locale.
  return formatDayMonthShort(then);
}
